/* -----------------Globals--------------- */
import { ScrollView } from "react-native";

/* -----------------Helpers & Hooks--------------- */
import useSessionConfig, {
	type AdditionalParams,
} from "../hooks/useSessionConfig";

import type { SessionConfigFiltersType } from "@configs/custom-app-config-schema";
/* -----------------Child components--------------- */
import { NestedExamChipsContainer } from "@features/dashboard/containers/NestedExamChipsContainer";
import { diagnosticTagIds } from "@features/session/helpers/session";
import { useAppStore } from "@hooks/useAppStore";
import type { Distribution } from "@memorang/types";
import type { PracticeTestExam } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import StickyButtonContainer from "@memorang/ui/components/StickyButtonContainer";
import { useDevice } from "@memorang/ui/useDevice";
import type { DiagnosticItem } from "api/types";
import ModalHeader from "components/ModalHeader";
/* -----------------Child components--------------- */
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";
import { useMemo } from "react";
import DistributionFilterList from "../components/DistributionFilterList";
import ItemSlider from "../components/ItemSlider";
import ItemTypesSelector from "../components/ItemTypesSelector";
import TagFiltersList from "../components/TagFiltersList";
import useLoadingMessages from "../hooks/useLoadingMessages";
import { LearningModes } from "../types";
import SessionSummary from "./SessionSummary";

type Props = {
	available: Distribution;
	additionalParams?: AdditionalParams;
	handleCreateSession: (distribution: Distribution, tags: number[]) => void;
	creatingSession?: boolean;
	handleShowPurchase: () => void;
	handleClose?: () => void;
	hasAccess?: boolean;
	diagnosticItems?: DiagnosticItem[];
	exams?: PracticeTestExam[];
	distributionFilterItems: SessionConfigFiltersType;
};

const SessionConfig = ({
	available,
	additionalParams,
	handleCreateSession,
	creatingSession,
	handleShowPurchase,
	hasAccess,
	exams,
	handleClose,
	distributionFilterItems,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: fix me later
}: Props) => {
	const showLearningModes = useAppStore(
		(state) => state.app.sessionConfig?.showLearningMode,
	);
	const { isMobileWeb, isMobile } = useDevice();
	const showDistributionFilters = true;
	const showItemsSlider = useAppStore(
		(state) => state.app.sessionConfig?.showItemsSlider,
	);
	const {
		handleSetTagCheckboxMap,
		selectedTagCheckboxMap,
		numRequiredItems,
		requiredDistribution,
		selectedLearningMode,
		handleSetLearningMode,
		finalAvailableDist,
		selectedDistributionCheckboxMap,
		handleDistributionCheckboxState,
		availableItems,
		numTotalItemsFiltered,
		handleSliderChange,
	} = useSessionConfig({
		availableDistribution: available,
		additionalParams: additionalParams || {},
		isSessionCreationInProgress: creatingSession,
		showLearningMode: showLearningModes,
		showDistributionFilters,
		distributionFilterItems,
	});

	const ctaText = useLoadingMessages(Boolean(creatingSession));

	const filterSelectedDiagnosticTags =
		selectedTagCheckboxMap &&
		Object.keys(selectedTagCheckboxMap).filter(
			(value) =>
				selectedTagCheckboxMap[value] &&
				Object.values(diagnosticTagIds).includes(Number(value)),
		);

	const isDiagnosticTagSelected =
		filterSelectedDiagnosticTags && filterSelectedDiagnosticTags.length > 0;

	const handleStartSession = () => {
		const selectedTags = selectedTagCheckboxMap
			? Object.keys(selectedTagCheckboxMap).filter(
					(tagId) => selectedTagCheckboxMap[tagId],
				)
			: [];

		const unselectedTags =
			additionalParams?.hasAccess && selectedTagCheckboxMap
				? Object.keys(selectedTagCheckboxMap || {}).filter(
						(tagId) => !selectedTagCheckboxMap[tagId],
					)
				: additionalParams?.freeTagIds;
		const finalTagIds =
			selectedTags?.length > 0 ? selectedTags : unselectedTags;

		if (!creatingSession) {
			handleCreateSession(requiredDistribution, finalTagIds?.map(Number) || []);
		}
	};

	const filteredSectionTags = useMemo(() => {
		if (!additionalParams?.sectionTags) {
			return undefined;
		}
		if (selectedLearningMode === LearningModes.essays) {
			return additionalParams.sectionTags.filter(
				(section) => section.title.toLowerCase() === "writing",
			);
		}
		return additionalParams.sectionTags.filter(
			(section) => section.title.toLowerCase() !== "writing",
		);
	}, [additionalParams?.sectionTags, selectedLearningMode]);

	const showSummary = numRequiredItems > 0;

	return (
		<StickyButtonContainer
			buttonText={ctaText}
			icon={creatingSession ? undefined : "play-outline"}
			onPress={handleStartSession}
			loading={creatingSession}
			paddingHorizontal={isMobile ? 16 : 24}
			showButton={!(!hasAccess && exams?.length)}
			disabled={!numRequiredItems || creatingSession}
		>
			{isMobileWeb && !handleClose && (
				<ModalHeader backIcon="arrow-left" headerTitle="Study session" />
			)}
			<ScrollView
				contentContainerStyle={{
					paddingVertical: 16,
					paddingHorizontal: isMobile ? 0 : 8,
				}}
			>
				<Box flex={1}>
					<NestedExamChipsContainer
						disabled={creatingSession}
						variant="radio"
					/>
					{!showLearningModes && (
						<DiagnosticItemsContainer
							exams={exams}
							hasAccess={hasAccess}
							handleClose={handleClose}
							handleShowPurchase={handleShowPurchase}
							paddingHorizontal={16}
							isSessionConfig
							disableAll={creatingSession}
						/>
					)}
					{showLearningModes && additionalParams?.numItemsMap ? (
						<ItemTypesSelector
							selectedLearningMode={selectedLearningMode}
							disableAll={creatingSession}
							handleSetLearningMode={handleSetLearningMode}
							numItemsMap={additionalParams.numItemsMap}
						/>
					) : null}
					{showItemsSlider && (
						<ItemSlider
							label={"Session length"}
							availableItems={availableItems}
							requiredItems={numRequiredItems}
							disabled={creatingSession}
							mode={selectedLearningMode}
							numTotalItemsFiltered={numTotalItemsFiltered}
							handleSliderChange={handleSliderChange}
							testID="session-length-slider"
						/>
					)}
					{showDistributionFilters && (
						<DistributionFilterList
							distribution={finalAvailableDist}
							hasAccess={
								hasAccess || Boolean(additionalParams?.freeTagIds?.length)
							}
							disableAll={creatingSession}
							selectedCheckboxMap={selectedDistributionCheckboxMap}
							handleSelectCheckbox={handleDistributionCheckboxState}
							distributionFilterItems={distributionFilterItems}
							handleShowPurchase={handleShowPurchase}
						/>
					)}
					{filteredSectionTags ? (
						<TagFiltersList
							disableAll={creatingSession || isDiagnosticTagSelected}
							selectedCheckboxMap={selectedTagCheckboxMap}
							handleSelectCheckbox={(distKey, locked) => {
								if (locked) {
									handleShowPurchase();
								} else {
									handleSetTagCheckboxMap(distKey);
								}
							}}
							hasAccess={hasAccess}
							showPremiumChip={!hasAccess}
							sectionTags={filteredSectionTags}
						/>
					) : null}
				</Box>
			</ScrollView>
			{showSummary && (
				<SessionSummary
					numRequiredItems={numRequiredItems}
					selectedDistributionCheckboxMap={selectedDistributionCheckboxMap}
					selectedTagCheckboxMap={selectedTagCheckboxMap}
					sectionTags={filteredSectionTags}
				/>
			)}
		</StickyButtonContainer>
	);
};

export default SessionConfig;
